import * as Sentry from '@sentry/nextjs'

import { useBulkResolveStaleIncidentsMutation } from '@/graphql/generated/hooks'
import type { BulkResolveStaleIncidentsMutation } from '@/graphql/generated/operations'
import {
  StaleResolveScope,
  StaleResolveSkipReason,
} from '@/graphql/generated/schemas'
import { trackEvent } from '@/utils/analytics'

import { TEAM_STALE_WORK_ANALYTICS } from '../constants/constants'

/** One skipped incident from the mutation's skip-and-report payload. */
export type StaleResolveSkipped = NonNullable<
  BulkResolveStaleIncidentsMutation['bulkResolveStaleIncidents']
>['skipped'][number]

interface ResolveArgsIProps {
  incidentIds: string[]
  scope: StaleResolveScope
  message?: string
}

interface ResolveResultIProps {
  resolvedCount: number
  skipped: StaleResolveSkipped[]
}

interface UseStaleBulkResolveResultIProps {
  /** Rejects on mutation failure (after reporting); the caller shows the toast. */
  resolve: (args: ResolveArgsIProps) => Promise<ResolveResultIProps>
  isResolving: boolean
}

/**
 * Wraps the bulkResolveStaleIncidents mutation (ENG-32272): resolves the
 * given stale incidents with skip-and-report semantics; RESOLVED or missing
 * ids come back in `skipped` instead of failing the batch. Fires the
 * success/failure analytics here so every scope's flow reports identically;
 * the note text itself is never sent to analytics.
 */
export const useStaleBulkResolve = (): UseStaleBulkResolveResultIProps => {
  const [bulkResolveStaleIncidents, { loading: isResolving }] =
    useBulkResolveStaleIncidentsMutation()

  const resolve = async ({
    incidentIds,
    scope,
    message,
  }: ResolveArgsIProps): Promise<ResolveResultIProps> => {
    try {
      const { data } = await bulkResolveStaleIncidents({
        variables: {
          input: { incidentIds, message: message || undefined, scope },
        },
      })
      const payload = data?.bulkResolveStaleIncidents
      if (!payload) {
        // The schema types the payload as nullable; a missing payload with no
        // GraphQL error must not read as an empty success (the modal would
        // close with no toast). Throwing routes it through the failure path.
        throw new Error('bulkResolveStaleIncidents returned no payload')
      }
      const result = {
        resolvedCount: payload.resolvedIncidentActions?.length ?? 0,
        skipped: payload.skipped ?? [],
      }
      // One outcome event with counts rather than separate skip events:
      // partial resolves segment on skipped_count and break down by reason.
      const countByReason = (reason: StaleResolveSkipReason) =>
        result.skipped.filter((skip) => skip.reason === reason).length
      trackEvent(TEAM_STALE_WORK_ANALYTICS.bulkResolveSuccess, {
        incident_count: incidentIds.length,
        resolved_count: result.resolvedCount,
        scope,
        skipped_access_denied: countByReason(
          StaleResolveSkipReason.AccessDenied
        ),
        skipped_already_resolved: countByReason(
          StaleResolveSkipReason.AlreadyResolved
        ),
        skipped_count: result.skipped.length,
        skipped_not_found: countByReason(StaleResolveSkipReason.NotFound),
      })
      return result
    } catch (error) {
      Sentry.captureException(error, {
        extra: { incidentCount: incidentIds.length, scope },
        tags: { action: 'bulk_resolve', feature: 'stale_work' },
      })
      trackEvent(TEAM_STALE_WORK_ANALYTICS.bulkResolveFailure, {
        incident_count: incidentIds.length,
        scope,
      })
      throw error
    }
  }

  return { isResolving, resolve }
}
